import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './utils.js';

type Variant =
  'default' | 'accent' | 'secondary' | 'ghost' | 'destructive' | 'outline' | 'link';
type Size = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

const variantClasses: Record<Variant, string> = {
  default: 'bg-primary text-primary-foreground hover:bg-primary/90',
  // Sparing brand-blue CTA (≤ once per screen) for forward/positive actions —
  // e.g. the tool-screen done control (`t.toolScreen.done`). Standard primary stays
  // `default` (black).
  accent: 'bg-accent text-accent-foreground hover:bg-accent/90',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  ghost: 'hover:bg-accent hover:text-accent-foreground',
  destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  outline:
    'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
  link: 'text-accent underline-offset-4 hover:underline',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 rounded-md px-3 text-sm',
  md: 'h-9 rounded-md px-4 text-sm',
  lg: 'h-10 rounded-md px-6 text-base',
  icon: 'h-9 w-9 rounded-md',
  'icon-sm': 'h-8 w-8 rounded-md',
};

const baseClasses =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'default', size = 'md', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
      {...rest}
    />
  );
});
